import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { exportNotebookPdf, notebookPdfFileName } from './utils/notebookPdfExport';
import { triggerDownload } from '../../utils/exportUtils';
import './PdfExportDialog.css';

/**
 * "Export to PDF" dialog for a notebook (BBD-127), opened from the editor's
 * header and from the notebook list.
 *
 * All export state (options, progress) lives here, never in the editor.
 *
 * notebookId, title, pageCount: the notebook to export.
 * currentPage: position of the open page (starting at 1) when opened from the
 *   editor; adds the "Current page" choice.
 * background, localPages: the editor's current background and loaded pages
 *   (see exportNotebookPdf); both optional.
 */
function NotebookExportDialog({ notebookId, title, pageCount, currentPage = null, background = null, localPages = null, onClose }) {
    const total = Math.max(1, pageCount || 1);
    const [scope, setScope] = useState('all');
    const [rangeFrom, setRangeFrom] = useState(1);
    const [rangeTo, setRangeTo] = useState(total);
    const [includeBackground, setIncludeBackground] = useState(true);
    const [status, setStatus] = useState({ state: 'idle', message: '' });
    const [progress, setProgress] = useState(null);
    const abortRef = useRef(null);

    const working = status.state === 'working';

    // Stop a running export if the dialog goes away (leaving the notebook, for example)
    useEffect(() => () => {
        if (abortRef.current) abortRef.current.abort();
    }, []);

    const clampPage = (value) => Math.min(Math.max(parseInt(value, 10) || 1, 1), total);

    const selection = () => {
        if (scope === 'current') return { from: currentPage, to: currentPage };
        if (scope === 'range') {
            const from = clampPage(rangeFrom);
            const to = clampPage(rangeTo);
            return { from: Math.min(from, to), to: Math.max(from, to) };
        }
        return { from: 1, to: total };
    };

    const handleExport = async () => {
        const pages = selection();
        const controller = new AbortController();
        abortRef.current = controller;
        setProgress({ done: 0, total: pages.to - pages.from + 1 });
        setStatus({ state: 'working', message: '' });

        try {
            const result = await exportNotebookPdf({
                notebookId,
                // "All pages" is left open-ended: the notebook may have gained pages since the list loaded
                selection: scope === 'all' ? null : pages,
                includeBackground,
                background,
                localPages,
                onProgress: setProgress,
                signal: controller.signal,
            });

            const fileName = notebookPdfFileName(result.title || title, scope === 'all' ? null : pages, total);
            triggerDownload(result.blob, fileName, 'application/pdf');

            const skipped = result.skippedMedia > 0
                ? ` ${result.skippedMedia} embedded file${result.skippedMedia !== 1 ? 's' : ''} could not be included.`
                : '';
            setStatus({
                state: 'done',
                message: `Saved "${fileName}" (${result.pageCount} page${result.pageCount !== 1 ? 's' : ''}).${skipped}`,
            });
        } catch (err) {
            if (err?.name === 'AbortError') {
                setStatus({ state: 'idle', message: 'Export cancelled.' });
            } else {
                console.error('Notebook PDF export failed:', err);
                setStatus({ state: 'error', message: err?.message || 'The PDF could not be created. Please try again.' });
            }
        } finally {
            abortRef.current = null;
            setProgress(null);
        }
    };

    const handleCancel = () => {
        if (working) {
            if (abortRef.current) abortRef.current.abort();
        } else {
            onClose();
        }
    };

    // Typing in the dialog must not reach the editor's shortcuts (undo/redo)
    const stopKeys = (e) => e.stopPropagation();

    return createPortal(
        <div className="NotebookPdfExport__overlay" onClick={() => { if (!working) onClose(); }} onKeyDown={stopKeys}>
            <div
                className="NotebookPdfExport__dialog"
                role="dialog"
                aria-modal="true"
                aria-label="Export to PDF"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="NotebookPdfExport__header">
                    <h2>Export to PDF</h2>
                    <button className="NotebookPdfExport__close" onClick={onClose} disabled={working} aria-label="Close">
                        &times;
                    </button>
                </div>

                <div className="NotebookPdfExport__body">
                    <p className="NotebookPdfExport__name" title={title}>{title}</p>

                    <fieldset className="NotebookPdfExport__fieldset" disabled={working}>
                        {currentPage !== null && (
                            <label className="NotebookPdfExport__choice">
                                <input
                                    type="radio"
                                    name="notebook-export-scope"
                                    checked={scope === 'current'}
                                    onChange={() => setScope('current')}
                                />
                                Current page (page {currentPage})
                            </label>
                        )}
                        <label className="NotebookPdfExport__choice">
                            <input
                                type="radio"
                                name="notebook-export-scope"
                                checked={scope === 'range'}
                                onChange={() => setScope('range')}
                            />
                            Pages
                            <input
                                type="number"
                                min="1"
                                max={total}
                                value={rangeFrom}
                                onFocus={() => setScope('range')}
                                onChange={(e) => setRangeFrom(e.target.value)}
                                aria-label="First page"
                            />
                            to
                            <input
                                type="number"
                                min="1"
                                max={total}
                                value={rangeTo}
                                onFocus={() => setScope('range')}
                                onChange={(e) => setRangeTo(e.target.value)}
                                aria-label="Last page"
                            />
                        </label>
                        <label className="NotebookPdfExport__choice">
                            <input
                                type="radio"
                                name="notebook-export-scope"
                                checked={scope === 'all'}
                                onChange={() => setScope('all')}
                            />
                            All pages ({total})
                        </label>

                        <hr className="NotebookPdfExport__divider" />

                        <label className="NotebookPdfExport__choice">
                            <input
                                type="checkbox"
                                checked={includeBackground}
                                onChange={(e) => setIncludeBackground(e.target.checked)}
                            />
                            Include the page background
                        </label>
                    </fieldset>

                    {!includeBackground && (
                        <p className="NotebookPdfExport__hint">
                            Pages are exported as ink on plain white, which is best for printing.
                        </p>
                    )}

                    {working && progress && (
                        <>
                            <div className="NotebookPdfExport__progress" aria-hidden="true">
                                <div
                                    className="NotebookPdfExport__progress-bar"
                                    style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                                />
                            </div>
                            <p className="NotebookPdfExport__status" role="status">
                                {progress.done < progress.total
                                    ? `Page ${progress.done + 1} of ${progress.total}...`
                                    : 'Finishing the file...'}
                            </p>
                        </>
                    )}

                    {status.message && (
                        <p className={`NotebookPdfExport__status NotebookPdfExport__status--${status.state}`} role="status">
                            {status.message}
                        </p>
                    )}
                </div>

                <div className="NotebookPdfExport__footer">
                    <button className="NotebookPdfExport__btn" onClick={handleCancel}>
                        {status.state === 'done' ? 'Close' : 'Cancel'}
                    </button>
                    <button className="NotebookPdfExport__btn NotebookPdfExport__btn--primary" onClick={handleExport} disabled={working}>
                        {working
                            ? <><i className="fas fa-spinner fa-spin"></i> Exporting...</>
                            : <><i className="fas fa-file-pdf"></i> Export</>}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default NotebookExportDialog;
